import { useEffect, useRef, useState } from "react";
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { useTranslation } from "react-i18next";
import RuleEngineBackend from "../services/RuleEngineBackend";
import { Dialog } from 'primereact/dialog';
import { toast } from "react-toastify";
import PPUtils from "../../utils/PPUtils"
import Select from "react-select"
import PPColumnSelectModal from "../common/PPColumnSelectModal";
import { FilterMatchMode } from "primereact/api";
import {
    buildApiFilters,
    appendChannelFilter,
    mapSortField,
    NUMERIC_FILTER_PLACEHOLDER,
    formatSentinelNumber,
    formatRuleDisplay,
    isDateFilterField,
} from "../common/tableQueryUtils";
import PPColumnDateFilter from "../common/PPColumnDateFilter";
import { getPrimeCalendarConfig } from "../common/primeCalendarSetup";
import { usePricemanTablePageSize } from "../common/usePricemanTablePageSize";
import { resolveChannelId, selectChannel } from "../common/priceManagementChannelStorage";
import { isExportCountWithinLimit, MAX_TABLE_EXPORT_ROWS } from "../common/tableExportConfig";
import "../../styles/priceman.css";
import { loadStoredJson, saveStoredJson, normalizeSortState, loadStoredStringArray, mergeColumnLayout, mergeTableFilters, loadStoredJsonObject } from "../common/tableStateStorage";
import { persistUserSettingsLocalstorage } from "../../utils/userSettingsLocalstorage";
import PPMatchingStatusBanner from "../../components/matching/PPMatchingStatusBanner";

const STORAGE_ACTIVE = "priceManagementHistoryColumns";
const STORAGE_ORDER = "priceManagementHistoryColumnOrder";
const STORAGE_SORT = "priceManagementHistorySort";
const DEFAULT_SORT = { sortField: "createDate", sortOrder: "desc" };

function getDefaultHistoryColumns(t) {
    return [
        {field: 'id', header: t("pricemanagement_page.pricehistory_page.table_header_id"), filterable: true, active: true},
        {field: 'fromProductnr', header: t("pricemanagement_page.pricehistory_page.table_header_fromProductnr"), filterable: true, active: true},
        {field: 'ean', header: t("pricemanagement_page.pricehistory_page.table_header_ean"), filterable: true, active: true},
        {field: 'productRanking', header: t("pricemanagement_page.pricehistory_page.table_header_productRanking"), filterable: true, active: true},
        {field: 'producer', header: t("pricemanagement_page.pricehistory_page.table_header_producer"), filterable: true, active: true},
        {field: 'ekPrice', header: t("pricemanagement_page.pricehistory_page.table_header_ekPrice"), filterable: true, active: true},
        {field: 'price', header: t("pricemanagement_page.pricehistory_page.table_header_price"), filterable: true, active: true},
        {field: 'lastPrice', header: t("pricemanagement_page.pricehistory_page.table_header_lastPrice"), filterable: true, active: true},
        {field: 'shopPrice', header: t("pricemanagement_page.pricehistory_page.table_header_shopPrice"), filterable: true, active: true},
        {field: 'shopTotalPrice', header: t("pricemanagement_page.pricehistory_page.table_header_shopTotalPrice"), filterable: true, active: true},
        {field: 'istNettoFaktor', header: t("pricemanagement_page.pricehistory_page.table_header_istNettoFaktor"), filterable: true, active: true},
        {field: 'nettoFaktor', header: t("pricemanagement_page.pricehistory_page.table_header_nettoFaktor"), filterable: true, active: true},
        {field: 'bruttoFaktor', header: t("pricemanagement_page.pricehistory_page.table_header_bruttoFaktor"), filterable: true, active: true},
        {field: 'ppCurrency', header: t("pricemanagement_page.pricehistory_page.table_header_currency"), filterable: true, active: true},
        {field: 'clientStock', header: t("pricemanagement_page.pricehistory_page.table_header_clientStock"), filterable: true, active: true},
        {field: 'stock', header: t("pricemanagement_page.pricehistory_page.table_header_stock"), filterable: true, active: true},
        {field: 'coupon', header: t("pricemanagement_page.pricehistory_page.table_header_coupon"), filterable: true, active: true},
        {field: 'ppShop', header: t("pricemanagement_page.pricehistory_page.table_header_ppShop"), filterable: true, active: true},
        {field: 'provider', header: t("pricemanagement_page.pricehistory_page.table_header_provider"), filterable: true, active: true},
        {field: 'lang', header: t("pricemanagement_page.pricehistory_page.table_header_lang"), filterable: true, active: true},
        {field: 'productname', header: t("pricemanagement_page.pricehistory_page.table_header_productname"), filterable: true, active: true},
        {field: 'toProductnr', header: t("pricemanagement_page.pricehistory_page.table_header_toProductnr"), filterable: true, active: true},
        {field: 'rule', header: t("pricemanagement_page.pricehistory_page.table_header_rule.name"), filterable: true, active: true},
        {field: 'approvedByUser', header: t("pricemanagement_page.pricehistory_page.table_header_confirmedBy"), filterable: true, active: true},
        {field: 'createDate', header: t("pricemanagement_page.pricehistory_page.table_header_creation_date"), filterable: true, active: true},
        {field: 'crawlDate', header: t("pricemanagement_page.pricehistory_page.table_header_crawlDate"), filterable: true, active: true},
        {field: 'exportDate', header: t("pricemanagement_page.pricehistory_page.table_header_createDate"), filterable: true, active: true},
        {field: 'failedRule', header: t("pricemanagement_page.pricehistory_page.table_header_failedRule.name"), filterable: true, active: true},
        {field: 'failureReason', header: t("pricemanagement_page.pricehistory_page.table_header_errorReason"), filterable: true, active: true},
        {field: 'staging', header: t("pricemanagement_page.pricehistory_page.table_header_staging"), filterable: true, active: true},
    ];
}

function loadHistoryColumns(t) {
    const defaults = getDefaultHistoryColumns(t);
    const savedOrder = loadStoredStringArray(STORAGE_ORDER);
    const savedActive = loadStoredStringArray(STORAGE_ACTIVE);
    return mergeColumnLayout(defaults, savedOrder, savedActive);
}

function getHistorySortableFields(t) {
    return getDefaultHistoryColumns(t).map((c) => mapSortField(c.field) || c.field);
}

function loadSortFromStorage(t) {
    return normalizeSortState(loadStoredJson(STORAGE_SORT, null), getHistorySortableFields(t), DEFAULT_SORT);
}

export default function PPPriceManagementHistory() {

     let auth = PPUtils.getLogin()
     const token = auth.token;
     const customerId = PPUtils.getEffectiveCustomerId(auth);

     const { t, i18n } = useTranslation()
     const tableWrapRef = useRef(null);
     const pageSize = usePricemanTablePageSize(tableWrapRef, { defaultRows: 20 });

     const [columns, setColumns] = useState(() => loadHistoryColumns(t));
     const [channels, setChannels] = useState([]);
     const [tableData, setTableData] = useState([]);
     const [selectedChannelID, setSelectedChannelID] = useState(null);
     const [failedPriceadjustmentData, setFailedPriceadjustmentData] = useState([]);
     const [discontinuedItemsOption, setDiscontinuedItemsOption] = useState(0);
     const [failedPriceADJLoading, setFailedPriceADJLoading] = useState(false);
     const [columnModalVisible, setColumnModalVisible] = useState(false);
     const activeColumns = columns.filter((c) => c.active);

     const [first, setFirst] = useState(0);
     const [totalRecords, setTotalRecords] = useState(0);
     const [loading, setLoading] = useState(false);
     const [sortField, setSortField] = useState(() => loadSortFromStorage(t).sortField);
     const [sortOrder, setSortOrder] = useState(() => loadSortFromStorage(t).sortOrder);

     const [failedPriceadjustmentFiler, setFailedPriceadjustmentFiler] = useState({
          'id': { value: null, matchMode: FilterMatchMode.CONTAINS },
          'productnr': { value: null, matchMode: FilterMatchMode.STARTS_WITH },
          'rule.description': { value: null, matchMode: FilterMatchMode.CONTAINS },
          'lang': { value: null, matchMode: FilterMatchMode.CONTAINS },
          'failReason': { value: null, matchMode: FilterMatchMode.CONTAINS },
          'failureDate': { value: null, matchMode: FilterMatchMode.DATE_IS },
     });

     const buildEmptyFilters = () => {
          const o = {};
          const prefixFields = new Set(["fromProductnr", "toProductnr"]);
          getDefaultHistoryColumns(t).forEach((c) => {
               if (c.filterable) {
                    o[c.field] = {
                         value: null,
                         matchMode: prefixFields.has(c.field) ? FilterMatchMode.STARTS_WITH : FilterMatchMode.CONTAINS,
                    };
               }
          });
          return o;
     };

     const [filters, setFilters] = useState(buildEmptyFilters);

     useEffect(() => {
          const savedState = loadStoredJsonObject("historytable-state-local");
          if (savedState == null) {
               return;
          }
          setFilters(mergeTableFilters(buildEmptyFilters(), savedState.filters));
     }, []);

     useEffect(() => {
          saveStoredJson("historytable-state-local", { filters });
          persistUserSettingsLocalstorage();
     }, [filters]);

     const onFilterChange = (e, field) => {
          const value = e.target.value.trim() === "" ? null : e.target.value;
          const prefixFields = new Set(["fromProductnr", "toProductnr"]);
          setFilters((prev) => ({
               ...prev,
               [field]: {
                    value,
                    matchMode: prefixFields.has(field) ? FilterMatchMode.STARTS_WITH : FilterMatchMode.CONTAINS,
               },
          }));
     };

     const onDateFilterChange = (field, value) => {
          setFilters((prev) => ({
               ...prev,
               [field]: { value: value ?? null, matchMode: "custom" },
          }));
     };

     const onModalDateFilterChange = (field, value) => {
          setFailedPriceadjustmentFiler((prev) => ({
               ...prev,
               [field]: { value: value ?? null, matchMode: FilterMatchMode.DATE_IS },
          }));
     };

     const onModalFilterChange = (e, field) => {
          const value = e.target.value;
          setFailedPriceadjustmentFiler((prev) => ({
               ...prev,
               [field]: { ...prev[field], value },
          }));
     }

     function resetAllFilters() {
          const resetFilters = buildEmptyFilters();
          setFilters(resetFilters);
          saveStoredJson("historytable-state-local", { filters: resetFilters });
          setSortField(DEFAULT_SORT.sortField);
          setSortOrder(DEFAULT_SORT.sortOrder);
          saveStoredJson(STORAGE_SORT, DEFAULT_SORT);
          persistUserSettingsLocalstorage();
     }



     const buildAllFilters = () => {
          const extra = {};
          if (discontinuedItemsOption === 1) extra.hideDiscontinued = true;
          else if (discontinuedItemsOption === 2) extra.onlyDiscontinued = true;
          const apiFilters = buildApiFilters(filters, { extra });
          return appendChannelFilter(apiFilters, selectedChannelID);
     }

     const setTableColumns = (selection) => {
          const selectedFields = selection.map((sel) => sel.value);
          const selectedSet = new Set(selectedFields);
          const byField = Object.fromEntries(columns.map((c) => [c.field, c]));
          const ordered = [];
          selectedFields.forEach((f) => {
               if (byField[f]) ordered.push({ ...byField[f], active: true });
          });
          columns.forEach((c) => {
               if (!selectedSet.has(c.field)) ordered.push({ ...c, active: false });
          });
          setColumns(ordered);
          saveStoredJson(STORAGE_ACTIVE, selectedFields);
          saveStoredJson(STORAGE_ORDER, ordered.map((c) => c.field));
          persistUserSettingsLocalstorage();
     }

     const onColumnReorder = (event) => {
          const activeIndices = columns.reduce((indices, column, index) => {
               if (column.active) indices.push(index);
               return indices;
          }, []);
          const from = activeIndices[event.dragIndex];
          const to = activeIndices[event.dropIndex];
          if (from === undefined || to === undefined) return;
          const next = [...columns];
          const [moved] = next.splice(from, 1);
          next.splice(to, 0, moved);
          setColumns(next);
          saveStoredJson(STORAGE_ORDER, next.map((column) => column.field));
          persistUserSettingsLocalstorage();
     }

     const onPageChange = (event) => {
          setFirst(event.first);
          window.scrollTo(0, 0);
     }

     useEffect(() => {
          setFirst(0);
     }, [pageSize]);

     const onSort = (event) => {
          let nextField;
          let nextOrder;
          if (event.sortField) {
               nextField = event.sortField;
               nextOrder = event.sortOrder === 1 ? "asc" : "desc";
          } else {
               nextField = "createDate";
               nextOrder = "desc";
          }
          setSortField(nextField);
          setSortOrder(nextOrder);
          saveStoredJson(STORAGE_SORT, { sortField: nextField, sortOrder: nextOrder });
          persistUserSettingsLocalstorage();
     }

     useEffect(() => {
          setFirst(0);
     }, [filters, discontinuedItemsOption, selectedChannelID]);

     useEffect(() => {
          let cancelled = false;

          async function fetchData() {
               if (selectedChannelID == null) {
                    setTableData([]);
                    setTotalRecords(0);
                    return;
               }

               setLoading(true);
               const allFilters = buildAllFilters();
               const count = await RuleEngineBackend.getCalculatedPricesCount(customerId, token, allFilters);
               if (cancelled) {
                    return;
               }

               const total = count ?? 0;
               setTotalRecords(total);

               const maxPage = total > 0 ? Math.ceil(total / pageSize) - 1 : 0;
               const pageNumber = Math.min(Math.floor(first / pageSize), maxPage);

               if (pageNumber !== Math.floor(first / pageSize)) {
                    setFirst(pageNumber * pageSize);
                    setLoading(false);
                    return;
               }

               const calculatedPrices = await RuleEngineBackend.getCalculatedPrices(
                    customerId,
                    token,
                    pageNumber,
                    pageSize,
                    allFilters,
                    mapSortField(sortField),
                    sortOrder
               );
               if (cancelled) {
                    return;
               }

               setTableData(calculatedPrices ?? []);
               setLoading(false);
          }

          fetchData();
          return () => {
               cancelled = true;
          };
     }, [customerId, token, discontinuedItemsOption, first, pageSize, filters, sortField, sortOrder, selectedChannelID]);

     useEffect(() => {
          async function fetchChannels() {
               const allChannels = await RuleEngineBackend.getChannels(customerId, token);
               if (allChannels) {
                    const channelList = Array.isArray(allChannels) ? allChannels : [allChannels];
                    const visible = channelList.filter((c) => !c.hidden);
                    setChannels(visible.length > 0 ? visible : channelList);
                    if (selectedChannelID == null || !channelList.some((c) => Number(c.id) === Number(selectedChannelID))) {
                         const resolvedChannelId = resolveChannelId(channelList, customerId, selectedChannelID);
                         setSelectedChannelID(resolvedChannelId);
                    }
               }
          }
          fetchChannels();
     }, [customerId, token]);


     const renderColumnFilter = (field, header) => {
          if (isDateFilterField(field)) {
               const { locale, dateFormat } = getPrimeCalendarConfig(i18n.language);
               return (
                    <PPColumnDateFilter
                         value={filters[field]?.value}
                         onChange={(value) => onDateFilterChange(field, value)}
                         placeholder={header}
                         locale={locale}
                         dateFormat={dateFormat}
                    />
               );
          }
          const isNumeric = ["id", "price", "lastPrice", "shopPrice", "shopTotalPrice", "ekPrice", "nettoFaktor", "istNettoFaktor", "bruttoFaktor", "productRanking", "coupon"].includes(field);
          return (
              <input
                  type="text"
                  value={filters[field]?.value || ''}
                  onChange={(e) => onFilterChange(e, field)}
                  placeholder={isNumeric ? NUMERIC_FILTER_PLACEHOLDER : header}
                  className="p-inputtext p-column-filter"
                  style={{
                      width: "100%",
                      fontSize: "12px",
                      padding: "6px 8px",
                      border: "1px solid #ced4da",
                      borderRadius: "4px"
                  }}
              />
          );
     };

     const renderModalColumnFilter = (field, header) => {
          if (isDateFilterField(field)) {
               const { locale, dateFormat } = getPrimeCalendarConfig(i18n.language);
               return (
                    <PPColumnDateFilter
                         value={failedPriceadjustmentFiler[field]?.value}
                         onChange={(value) => onModalDateFilterChange(field, value)}
                         placeholder={header}
                         locale={locale}
                         dateFormat={dateFormat}
                    />
               );
          }
          return (
               <input
                   type="text"
                   value={failedPriceadjustmentFiler[field]?.value || ''}
                   onChange={(e) => onModalFilterChange(e, field)}
                   placeholder={`${header}`}
                   className="p-inputtext p-column-filter"
                   style={{
                       width: "100%",
                       fontSize: "12px",
                       padding: "6px 8px",
                       border: "1px solid #ced4da",
                       borderRadius: "4px"
                   }}
               />
          );
     }

     async function showFailedPriceadjustment() {
          if (selectedChannelID == null || selectedChannelID < 0) {
               toast.error(t("pricemanagement_page.pricehistory_page.toast_error_choose_channel"));
               return;
          }
          setFailedPriceADJLoading(true);
          try {
               const data = await RuleEngineBackend.getFailedCalculations(customerId, token, selectedChannelID);
               if (data) {
                    setFailedPriceadjustmentData(Array.isArray(data) ? data : data.content || []);
               }
          } finally {
               setFailedPriceADJLoading(false);
          }
     }

     async function downloadCSV() {
          if (!isExportCountWithinLimit(totalRecords)) {
               toast.error(t("pricemanagement_page.price_checklist.export_limit_exceeded", {
                    count: totalRecords,
                    max: MAX_TABLE_EXPORT_ROWS,
               }));
               return;
          }
          const result = await RuleEngineBackend.getCalculatedPricesAsCSV(customerId, token, buildAllFilters());
          if (result?.success) {
               toast.success(t("pricemanagement_page.price_checklist.export_started"));
          } else {
               toast.error(t("pricemanagement_page.price_checklist.export_failed", {
                    message: result?.error || t("pricemanagement_page.overview_page.toast_error"),
               }));
          }
     }

     const historyBody = (field) => {
          if (field === "rule" || field === "failedRule") return (row) => formatRuleDisplay(row[field]);
          if (field === "ppCurrency") return (row) => row.ppCurrency?.currency ?? "";
          if (field === "ppShop") return (row) => row.ppShop?.name ?? row.ppShop?.domain ?? "";
          if (field === "approvedByUser") return (row) => row.approvedByUser?.name ?? row.approvedByUser?.username ?? "";
          if (field === "nettoFaktor" || field === "istNettoFaktor") return (row) => formatSentinelNumber(row[field]);
          if (field === "createDate" || field === "crawlDate" || field === "exportDate") return (row) => PPUtils.formatDateDE(row[field], true);
          return undefined;
     };

     const modalColumns = [
          {field: 'id', header: t("pricemanagement_page.pricehistory_page.table_header_id"), filterable: true},
          {field: 'productnr', header: t("pricemanagement_page.pricehistory_page.table_header_fromProductnr"), filterable: true},
          {field: 'rule.description', header: t("pricemanagement_page.pricehistory_page.table_header_rule.name"), filterable: true},
          {field: 'lang', header: t("single.language"), filterable: true},
          {field: 'failReason', header: t("pricemanagement_page.pricehistory_page.table_header_errorReason"), filterable: true},
          {field: 'failureDate', header: t("single.date"), filterable: true},
     ]

     if (customerId == null) {
          return (
               <div className="container-fluid priceman-page">
                    <div className="card shadow">
                         <div className="card-body">
                              <PPMatchingStatusBanner variant="no_customer" />
                         </div>
                    </div>
               </div>
          )
     }

     return (
          <>
               <div className="container-fluid priceman-page">
                    <div className="row">
                         <div className="col-12">
                              <div className="card shadow">
                                   <div className="card-body">
                                        <div className='p-3 d-flex flex-column priceman-page-body'>
                                             <div className="d-flex justify-content-between align-items-center">
                                                  <div className="d-flex flex-row w-75 justify-content-between align-items-center">
                                                       <Select
                                                                 className="priceman-channel-select"
                                                                 isClearable={false}
                                                                 value={channels.map((channel) => ({ label: channel.channelName, id: channel.id, value: channel.id })).find((o) => o.id === selectedChannelID) || null}
                                                                 onChange={(event) => selectChannel(customerId, event ? event.id : null, setSelectedChannelID)}
                                                                 options={channels.map((channel) => ({ label: channel.channelName, id: channel.id, value: channel.id }))}
                                                       />
                                                       <div className="col-md-6">
                                                            <button
                                                                 type="button" 
                                                                 className="btn btn-primary btn-lg" 
                                                                 onClick={showFailedPriceadjustment}
                                                                 disabled={selectedChannelID == null || selectedChannelID < 0}
                                                            >
                                                                 { failedPriceADJLoading && <output className="spinner-border spinner-border-sm" aria-hidden="true" /> }
                                                                 <span className="ms-2">
                                                                      {t("pricemanagement_page.pricehistory_page.action_show_failed_priceadjustment")}
                                                                 </span>
                                                            </button>
                                                       </div>
                                                       <div className="col-md-3">
                                                            <div className="d-flex flex-row align-items-center justify-content-center">
                                                                 <p className="text-md me-3 mb-0">Auslaufartikel:</p>
                                                                 <div className="btn-group btn-group" role="group">
                                                                      <button type="button" className={ discontinuedItemsOption == 0 ? "btn btn-primary": "btn btn-outline-primary"} onClick={() => setDiscontinuedItemsOption(0)}>{t("single.show")}</button>
                                                                      <button type="button" className={ discontinuedItemsOption == 1 ? "btn btn-primary": "btn btn-outline-primary"} onClick={() => setDiscontinuedItemsOption(1)}>{t("single.hide")}</button>
                                                                      <button type="button" className={ discontinuedItemsOption == 2 ? "btn btn-primary": "btn btn-outline-primary"} onClick={() => setDiscontinuedItemsOption(2)}>{t("single.extract")}</button>
                                                                 </div>
                                                            </div>
                                                       </div>
                                                  </div>

                                                  <div className="d-flex flex-row" style={{"gap": "10px"}}>
                                                       <button onClick={resetAllFilters} type="button" className="btn btn-outline-primary">{t("pricemanagement_page.overview_page.action_reset_filters")}</button>
                                                       <button className="btn btn-primary" onClick={() => setColumnModalVisible(true)} title={t("pricemanagement_page.price_checklist.column_picker_title")}><i className="fas fa-columns"></i></button>
                                                       <button onClick={downloadCSV} type="button" className="btn btn-primary">Export</button>
                                                  </div>
                                             </div>

                                             <div className="d-flex flex-row mt-2 priceman-table-row">
                                                  <div className="col-12 priceman">
                                                       <div className="priceman-table-wrap priceman-table-wrap--paged" ref={tableWrapRef}>
                                                            <DataTable 
                                                       value={tableData}
                                                       filters={filters}
                                                       onFilter={(e) => setFilters(e.filters)}
                                                       onSort={onSort}
                                                       sortField={sortField}
                                                       sortOrder={sortOrder === "asc" ? 1 : -1}
                                                       filterDisplay="row" 
                                                       sortMode="single"
                                                       size="small"
                                                       reorderableColumns
                                                       onColReorder={onColumnReorder}
                                                       resizableColumns 
                                                       removableSort
                                                       stripedRows
                                                       lazy
                                                       paginator
                                                       rows={pageSize}
                                                       totalRecords={totalRecords}
                                                       onPage={onPageChange}
                                                       first={first}
                                                       loading={loading}
                                                       scrollable
                                                       scrollHeight="flex"
                                                  >
                                                       {activeColumns.map((col, i) => (
                                                            <Column
                                                                 key={`main-table-${col.field}-${i}`}
                                                                 field={col.field}
                                                                 sortField={mapSortField(col.field) || col.field}
                                                                 body={historyBody(col.field)}
                                                                 header={col.header}
                                                                 headerStyle={{
                                                                      border: "1px solid #ddd",
                                                                      backgroundColor: "#f8f9fa",
                                                                      fontWeight: "600",
                                                                      fontSize: "14px",
                                                                      padding: "12px 8px",
                                                                      textAlign: "center",
                                                                      color: "#495057",
                                                                      verticalAlign: "middle"
                                                                 }}
                                                                 filterHeaderStyle={{
                                                                      border: "1px solid #ddd",
                                                                      backgroundColor: "#ffffff",
                                                                      padding: "8px"
                                                                 }}
                                                                 sortable={true}
                                                                 showFilterMenu={false}
                                                                 filter={col.filterable}
                                                                 filterMatchModeOptions={[
                                                                      { label: 'contains', value: FilterMatchMode.CONTAINS }
                                                                 ]}
                                                                 filterElement={renderColumnFilter(col.field, col.header)}
                                                                 style={{ minWidth: '9rem' }}
                                                            />
                                                       ))}
                                                  </DataTable>
                                                       </div>
                                                  </div>
                                             </div>
                                        </div>
                                   </div>
                              </div>
                         </div>
                    </div>
               </div>

               {/* Modal */}
               <Dialog
                    header={t("pricemanagement_page.pricehistory_page.action_show_failed_priceadjustment")}
                    footer={
                         <button className="btn btn-danger btn-lg m-2" onClick={() => {setFailedPriceadjustmentData([]); setFailedPriceADJLoading(false);}}>
                              {t("single.close")}
                         </button>
                    }
                    visible={failedPriceadjustmentData.length > 0} 
                    style={{ width: '70vw' }} 
                    onHide={() => {
                         if (failedPriceadjustmentData.length === 0) return; 
                         setFailedPriceadjustmentData([]); 
                         setFailedPriceADJLoading(false);
                    }}
               >
                    <DataTable 
                         value={failedPriceadjustmentData}
                         filters={failedPriceadjustmentFiler}
                         onFilter={(e) => setFailedPriceadjustmentFiler(e.filters)}
                         filterDisplay="row" 
                         sortMode="single"
                         paginator
                         rows={25}
                         rowsPerPageOptions={[25, 50, 100]}
                         reorderableColumns 
                         resizableColumns
                         stripedRows
                         size="small"
                         scrollable
                         scrollHeight="60vh"
                    >
                         {modalColumns.map((col, i) => (
                              <Column
                                   key={`modal-table-${col.field}-${i}`}
                                   field={col.field}
                                   dataType={col.field === "failureDate" ? "date" : undefined}
                                   header={col.header}
                                   headerStyle={{
                                        border: "1px solid #ddd",
                                        backgroundColor: "#f8f9fa",
                                        fontWeight: "600",
                                        fontSize: "14px",
                                        padding: "12px 8px",
                                        textAlign: "center",
                                        color: "#495057",
                                        verticalAlign: "middle"
                                   }}
                                   filterHeaderStyle={{
                                        border: "1px solid #ddd",
                                        backgroundColor: "#ffffff",
                                        padding: "8px"
                                   }}
                                   sortable={true}
                                   showFilterMenu={false}
                                   filter={col.filterable}
                                   filterElement={renderModalColumnFilter(col.field, col.header)}
                                   style={{ minWidth: '12rem' }}
                              />
                         ))}
                    </DataTable>
               </Dialog>

               <PPColumnSelectModal 
                    instanceId="spriceman_csm"
                    header={"Wähle die gewünschten Spalten für die Tabelle aus"}
                    options={columns}
                    setOption={setTableColumns}
                    visible={columnModalVisible}
                    onHide={() => setColumnModalVisible(false)}
               />
          </>
     );
}